import { ComponentProps, ForwardRefExoticComponent, RefAttributes, SVGProps } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  AttendanceCustomPeriodEmployeeItemFragment,
  AttendanceStateResolverStateEnum,
} from 'generated/resources/graphql'

import { buildUrl } from 'modules/core/lib/buildUrl'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import { getClockInStatusBadge } from 'modules/attendance/components/TimeTrackingCollection/components/ClockInStatusBadge'
import { aggregateMinutes } from 'modules/attendance/components/TimeTrackingCollection/lib/aggregateMinutes'
import { TimeTrackingCollectionEmployee } from 'modules/attendance/components/TimeTrackingCollection/types'

import { useAlerts } from './useAlerts'

type OneDataCollectionProps = ComponentProps<typeof OneDataCollection>
type Visualizations = OneDataCollectionProps['visualizations']
type TableVisualization = Extract<Visualizations[number], { type: 'table' }>
type ColumnsType = TableVisualization['options']['columns'][number]

type ColumnType = Omit<ColumnsType, 'render' | 'sorting'> & {
  render: (item: TimeTrackingCollectionEmployee) =>
    | string
    | { type: 'text'; value: string }
    | {
        type: 'person'
        value: {
          firstName: string
          lastName: string
          avatar: string | null | undefined
          badge?:
            | {
                type: 'critical' | 'positive' | 'warning'
                icon: ForwardRefExoticComponent<
                  Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>
                >
              }
            | undefined
        }
      }
    | {
        type: 'status'
        value: { status: string; label: string }
      }
    | {
        type: 'icon'
        value: {
          icon: ForwardRefExoticComponent<
            Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>
          >
          info: string
        }
      }
    | undefined
} & {
  sorting?: 'firstName' | 'lastName' | undefined
}

const reviewStatusToStatus = (
  reviewStatus: AttendanceStateResolverStateEnum | null | undefined
) => {
  if (!reviewStatus) {
    return {
      status: 'neutral',
      label: i18n.t('time_tracking.table_collection.review_status_in_progress'),
    }
  }

  switch (reviewStatus) {
    case 'approved':
      return {
        status: 'positive',
        label: i18n.t('time_tracking.table_collection.review_status_approved'),
      }
    case 'pending':
      return {
        status: 'info',
        label: i18n.t('time_tracking.table_collection.review_status_pending'),
      }
    default:
      return {
        status: 'neutral',
        label: i18n.t('time_tracking.table_collection.review_status_in_progress'),
      }
  }
}

export const useColumns = () => {
  const { alertsColumn } = useAlerts()

  const columns: ColumnType[] = [
    {
      label: i18n.t('time_tracking.table_collection.employee'),
      sorting: 'firstName',
      render: (item: TimeTrackingCollectionEmployee) => {
        const badge = getClockInStatusBadge(item.clockInStatus)

        return {
          type: 'person',
          value: {
            firstName: item.firstName,
            lastName: item.lastName,
            avatar: item.access.avatar,
            src: buildUrl(item.access.avatar ?? ''),
            badge: badge || undefined,
          },
        }
      },
    },
    ...alertsColumn,
    {
      label: i18n.t('time_tracking.table_collection.worked_with_planned_hours'),
      info: i18n.t('time_tracking.table_collection.effective_worked_with_planned_hours_tooltip'),
      render: (item: TimeTrackingCollectionEmployee) => {
        const baseline =
          item.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
            ?.extraHoursBaseline
        const { workedMinutes, plannedMinutes, dailyDays } = aggregateMinutes(item, baseline)

        const isDaily = item.isDaily
        if (isDaily) {
          return {
            type: 'text',
            value: i18n.t('time_tracking.day_abbreviation', {
              numberOfDays: dailyDays,
            }),
          }
        }

        return {
          type: 'text',
          value: `${minutesToText(workedMinutes, true)} / ${minutesToText(plannedMinutes, true)}`,
        }
      },
    },
    {
      label: i18n.t('time_tracking.table_collection.balance'),
      info: i18n.t('time_tracking.table_collection.balance_tooltip'),
      render: (item: TimeTrackingCollectionEmployee) => {
        const isDaily = item.isDaily
        if (isDaily) {
          return {
            type: 'text',
            value: '-',
          }
        }

        const baseline =
          item.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
            .extraHoursBaseline ?? undefined
        const { balanceMinutes } = aggregateMinutes(item, baseline)

        const zeroAlignment = balanceMinutes === 0 ? '\u2007' : ''

        return {
          type: 'text',
          value: `${zeroAlignment}${minutesToText(balanceMinutes, true, true)}`,
        }
      },
    },
    {
      label: i18n.t('time_tracking.table_collection.extra_hours'),
      info: i18n.t('time_tracking.table_collection.extra_hours_tooltip'),
      render: (item: TimeTrackingCollectionEmployee) => {
        const isDaily = item.isDaily
        if (isDaily) {
          return {
            type: 'text',
            value: '-',
          }
        }
        const baseline =
          item.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
            .extraHoursBaseline ?? undefined
        const { extraTime } = aggregateMinutes(item, baseline)
        const extraTimeMinutes = extraTime.minutes

        return extraTimeMinutes === undefined
          ? '-'
          : {
              type: 'text',
              value: minutesToText(extraTimeMinutes, true),
            }
      },
    },
    {
      label: i18n.t('time_tracking.table_collection.review_status'),
      render: (item: AttendanceCustomPeriodEmployeeItemFragment) => {
        return {
          type: 'status',
          value: reviewStatusToStatus(item.reviewStatus),
        }
      },
    },
  ]

  return {
    columns,
  }
}
